import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from "react";
import { DataAPIContext, GET_LIST } from "../../api";
import { debounce } from "../../utils";

const useStyles = makeStyles(theme => ({
    paper: {
        [theme.breakpoints.down('sm')]: {
            position: 'absolute',
            left: 0,
            right: 0,
            top: "3.75rem",
            maxHeight: '145px',
            display: 'flex',
        }
    },
    option: {
        width: '100%',

    },
    listbox: {
        width: '100%',
    },


}));
const RemoteAutocomplete = (props) => {
    const dataAPI = React.useContext(DataAPIContext);
    const [open, setOpen] = React.useState(false);
    const [staleOptions, setStaleOptions] = React.useState(true);
    const [options, setOptions] = React.useState([]);
    const [loadedEmptyOptions, setLoadedEmptyOptions] = React.useState(false);
    const loading = open && (staleOptions || (options.length <= 0 && !loadedEmptyOptions));
    const [query, setQuery] = React.useState(null);
    const inputRef = React.useRef(null);
    const urlSuffix = props.urlSuffix === void (0) ? "autocomplete/" : props.urlSuffix;
    const classes = useStyles()


    React.useEffect(() => {
        let active = true;
        const tm = setTimeout(async () => {
            const params = {
                convert: props.convert,
                filter: { query: props.query || query, filters: undefined },
                urlSuffix: urlSuffix
            }
            if (props.apiParams) {
                params.filter = { ...params.filter, ...props.apiParams };
            }
            if (props.apiFilters) {
                params.filter.filters = JSON.stringify(props.apiFilters)
            }
            const res = await dataAPI(GET_LIST, props.resource, params);

            if (active) {
                if (props.mapResults) {
                    setOptions(props.mapResults(res.data, res));
                } else {
                    setOptions(res.data);
                }
                setStaleOptions(false);
                if (res.data.length <= 0) {
                    setLoadedEmptyOptions(true);
                    props.setClientErrors?.(prev => ({ ...prev, userNotFound: true }))
                }
            }
        }, 500);

        if (!loading) {
            return undefined;
        }
        setLoadedEmptyOptions(false);

        return () => {
            active = false;
            clearTimeout(tm);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, query]);

    React.useEffect(() => {
        if (!open) {
            setOptions([]);
        }
    }, [open]);

    const acProps = {};
    if (props.multiple) {
        acProps.multiple = true;
    }
    if (props.disabled) {
        acProps.disabled = true;
    }
    if (props.hasOwnProperty('value')) {
        acProps.value = props.value;
    }
    if (props.hasOwnProperty('defaultValue')) {
        acProps.defaultValue = props.defaultValue;
    }
    if (props.style) {
        acProps.style = props.style;
    }
    if (props.width) {
        acProps.style = Object.assign({}, props.style, { width: `${props.width}px` });
    }
    if (props.fullWidth) {
        acProps.fullWidth = true;
    }
    const onQueryChange = debounce((ev) => {
        if (inputRef.current) {
            setStaleOptions(true);
            console.log('query change', inputRef.current.value);
            setQuery(inputRef.current.value);
            props.onQueryChange && props.onQueryChange(inputRef.current.value);
        }
    }, 300);

    const textFieldProps = { required: props.required, ...props.textFieldProps };

    const onChange = React.useCallback((ev, nv, reason) => {
        if (reason === "clear") {
            setQuery(null);
        }
        props.onChange && props.onChange(ev, nv, reason);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const values = props.value && props.value.constructor === Array ? props.value : (props.value ? [props.value] : []);
    const valuesIds = values.map(v => v?.id);

    const filteredOptions = props.filterSelected ? options.filter(opt => valuesIds.indexOf(opt.id) < 0) : options;

    const getOptionLabel = props.getOptionLabel || ((option) => (option && option.name ? option.name : ""));

    const getOptionSelected = (option, value) => {
        if (!value || !value.constructor) {
            return false;
        }
        if (acProps.multiple) {
            const selectedValues = value.constructor === Array ? value : [value];
            return selectedValues.some((selectedValue) => {
                const selectedId = (typeof selectedValue === "number" || typeof selectedValue === "string")
                    ? selectedValue
                    : selectedValue?.id;
                return selectedId === option?.id;
            });
        }
        if (typeof value === "number" || typeof value === "string") {
            return value === option?.id;
        }
        return value?.id === option?.id;
    };
    return (
        <Autocomplete
            {...acProps}
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            isOptionEqualToValue={props.getOptionSelected || getOptionSelected}
            getOptionLabel={getOptionLabel}
            renderOption={props.renderOption}
            freeSolo={true}
            options={filteredOptions}
            autoFocus
            filterOptions={props.filterOptions}
            loading={loading}
            onChange={onChange}
            classes={{ paper: classes.paper, option: classes.option, listbox: classes.listbox }}
            onBlur={props.onBlur}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...textFieldProps}
                    label={props.label}
                    variant={props.variant}
                    size={props.size}
                    onChange={onQueryChange}
                    inputRef={inputRef}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <React.Fragment>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                            </React.Fragment>
                        ),
                    }}
                />
            )}
        />
    );
}

export default RemoteAutocomplete;
